import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

// Proxied through this app's own server (routes/absenceReporting.js) to
// nano-fibre-fsm's /api/absence-reporting, where the submissions actually
// live — same reasoning as useSims/useDirectory's server-side proxy pattern.
// Always includes every still-open absence and every archived record, not
// just the recent ones (see FSM's listSubmissions), so Pending and Archived
// can both be built from this one list.
export function useAbsenceReportingSubmissions() {
  return useQuery({
    queryKey: ['absence-reporting-submissions'],
    queryFn: async () => {
      const { data } = await api.get('/api/absence-reporting');
      return data;
    },
  });
}

// PUT also proxies straight through to FSM's own updateSubmission — used by
// AbsenceReportingDashboard.jsx's far-right tick to persist
// dashboard_acknowledged (this app has no submissions data of its own to
// write to).
export function useUpdateAbsenceReportingSubmission() {
  return useMutation({
    mutationFn: async ({ id, ...payload }) => (await api.put(`/api/absence-reporting/${id}`, payload)).data,
  });
}

// One record's answers ({ id, submittedAt, answers }) — FSM's own
// getSubmission, for AbsenceRecordEditor.jsx to load before editing.
export function useAbsenceReportingSubmission(id, enabled) {
  return useQuery({
    queryKey: ['absence-reporting-submission', id],
    queryFn: async () => (await api.get(`/api/absence-reporting/${id}`)).data,
    enabled,
    staleTime: 0,
  });
}

// Editing an already-filed record never overwrites it — FSM's
// amendSubmission copies it into a new, amended entry (reason required)
// and marks the original superseded. Same call FSM's own form makes.
export function useAmendAbsenceReportingSubmission() {
  return useMutation({
    mutationFn: async ({ id, ...payload }) => (await api.post(`/api/absence-reporting/${id}/amend`, payload)).data,
  });
}

// Discard — the wrong record entirely (wrong person, pure mistake), proxied
// straight through to FSM's own archiveSubmission/unarchiveSubmission the
// same way every other call in this file is. Never deletes; drops the row
// out of every working list and out of reporting while keeping it visible
// in this app's own collapsed-by-default Archived section. Reversible.
export function useArchiveAbsenceReportingSubmission() {
  return useMutation({
    mutationFn: async (id) => (await api.put(`/api/absence-reporting/${id}/archive`)).data,
  });
}

export function useUnarchiveAbsenceReportingSubmission() {
  return useMutation({
    mutationFn: async (id) => (await api.put(`/api/absence-reporting/${id}/unarchive`)).data,
  });
}

// Follow-ups already sent to whoever reported this absence (FSM's
// absence_reporting_messages) — shown in the Message dialog so nobody
// chases the same person twice without knowing.
export function useSubmitterMessages(id, enabled) {
  return useQuery({
    queryKey: ['absence-reporting-messages', id],
    queryFn: async () => (await api.get(`/api/absence-reporting/${id}/messages`)).data,
    enabled,
  });
}

// Sends { channel: 'inbox' | 'email', message, employeeName } to whoever
// reported absence `id`. FSM picks the recipient from the submission
// itself, never from this payload.
export function useSendSubmitterMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }) => (await api.post(`/api/absence-reporting/${id}/message`, payload)).data,
    // The submissions list carries each record's last message (the
    // "Messaged by..." note on its row), so it's refreshed too.
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['absence-reporting-messages', id] });
      queryClient.invalidateQueries({ queryKey: ['absence-reporting-submissions'] });
    },
  });
}

// The branded PDF FSM generates per record (absenceReportPdf.js, FSM
// server side) — same call FSM's own AbsenceReportingList.jsx makes,
// proxied through this app's server the same way every other call in this
// file is (see routes/absenceReporting.js's forward()). Two separate
// reports per closed record, not one merged document: pass
// section: 'returnToWork' for the Return to Work one (a Return to Work is
// never asked on the Report Absence/Report Late form, so its answers never
// belong on that form's own report).
//
// The tab is opened FIRST, synchronously, before the await below — calling
// window.open after an await is no longer tied to the click that triggered
// it as far as the browser's concerned, and it opens a blank tab that never
// gets navigated (some browsers block it outright; others open it empty).
// Opening early and pointing it at the blob once it's ready keeps the tab
// tied to the original gesture either way.
export async function viewAbsenceReport(id, section) {
  const win = window.open('', '_blank');
  const query = section ? `?section=${encodeURIComponent(section)}` : '';
  const { data } = await api.get(`/api/absence-reporting/${id}/report.pdf${query}`, { responseType: 'blob' });
  const url = URL.createObjectURL(data);
  if (win) win.location.href = url;
}
